import React, { useMemo } from 'react';
import { Buildings } from '@phosphor-icons/react';
import type { ObjektOption } from '../../../lib/immobilien/objektOptionen';
import { SearchSelect } from './SearchSelect';

/**
 * Objekt-Dropdown der Listen (Mieter, Eigentümer; Handoff Mieter §5.1): Knopf mit gewähltem Objekt und
 * Anzahl, Popover mit Suche nach Objekt, Straße, Ort oder Nr.; „Alle Objekte“ steht immer oben.
 */
export function ObjektFilter({ optionen, wert, onChange, alleAnzahl }: {
  optionen: readonly ObjektOption[];
  /** Objekt-ID oder 'alle' */
  wert: string;
  onChange: (id: string) => void;
  /** Anzahl bei „Alle Objekte“ */
  alleAnzahl: number;
}) {
  const nach = useMemo(() => new Map(optionen.map(o => [o.id, o])), [optionen]);
  const gewaehlt = wert === 'alle' ? null : nach.get(wert) ?? null;
  return (
    <SearchSelect
      knopf={{
        ariaLabel: `Objekt wählen, aktuell ${gewaehlt ? gewaehlt.label : 'Alle Objekte'}`,
        icon: <Buildings weight="fill" size={16} color="var(--acc)" aria-hidden />,
        inhalt: (
          <>
            <span className="flex-1 min-w-0 truncate text-left">{gewaehlt ? gewaehlt.label : 'Alle Objekte'}</span>
            <span className="immo-zahl flex items-center" style={{ height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>
              {gewaehlt ? gewaehlt.anzahl : alleAnzahl}
            </span>
          </>
        ),
      }}
      fest={[{ id: 'alle', label: 'Alle Objekte', sub: `${optionen.length} Objekte` }]}
      optionen={optionen}
      gewaehltId={wert}
      onWahl={o => onChange(o.id)}
      platzhalter="Objekt, Straße, Ort oder Nr. suchen"
      leerText="Kein Objekt gefunden"
      zeile={(o, z) => {
        const opt = nach.get(o.id);
        const alle = o.id === 'alle';
        return (
          <>
            <span className="immo-zahl flex items-center justify-center shrink-0"
              style={{ width: 30, height: 30, borderRadius: 10, fontSize: alle ? 13 : 10, fontWeight: 800, background: z.gewaehlt ? 'var(--acc-fill)' : 'var(--fill2)', color: z.gewaehlt ? 'var(--acc-ink)' : 'var(--ink2)' }}>
              {alle ? 'Σ' : opt?.nummer || '–'}
            </span>
            <span className="flex flex-col flex-1 min-w-0">
              <span className="truncate" style={{ fontSize: 13, fontWeight: z.gewaehlt ? 800 : 600 }}>{o.label}</span>
              {o.sub && <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{o.sub}</span>}
            </span>
            <span className="immo-zahl" style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>{alle ? alleAnzahl : opt?.anzahl ?? 0}</span>
          </>
        );
      }}
    />
  );
}
